import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Store } from 'src/store';
import getPrice from 'src/store/price/selector/getPrice';
import getCurrencyId from 'src/store/price/selector/getCurrencyId';
import getPriceFormat from 'src/store/price/selector/getPriceFormat';
import fetchPricesOperation from 'src/store/price/operation/fetchPricesOperation';
import getProductTypeId from 'src/store/design/selector/getProductTypeId';
import getTotalPagesCount from 'src/store/design/selector/getTotalPagesCount';
import getMinPagesCount from 'src/store/design/selector/getMinPagesCount';
import getUsedClipArtsPrice from 'src/store/addon/selector/getUsedClipArtsPrice';
import getUpsellTotalPrice from 'src/store/upsell/selector/getUpsellTotalPrice';
import getProductBundleSelectedProductsTotalPrice from 'src/store/productBundle/selector/getProductBundleSelectedProductsTotalPrice';
import isFeatureEnabled from 'src/store/app/selector/isFeatureEnabled';
import { Feature } from 'src/store/app/types';
import calculateDesignPrice from 'src/util/price/calculateDesignPrice';
import addExtraPrice from 'src/util/price/addExtraPrice';
import { Price } from 'src/store/price/types';
import getStructure from 'src/store/design/selector/getStructure';
import { findSizeSelector, getVariantTypeNames, resolveSizeVariant } from 'src/util/apparel/sizeVariants';
import resolveBoundProduct, { getVariantAttributes } from 'src/util/design/resolveBoundProduct';
import { MultiSizeItem } from 'src/store/multisize/types';

const NO_EXTRA: Price = { price: 0, priceInitial: 0 };

export interface MultiSizeRow {
  value: string;
  name: string;
  productId?: number;
  available: boolean;
  qty: number;
  min: number;
}

export interface UseMultiSizeRows {
  rows: MultiSizeRow[];
  totalUnits: number;
  /** Discounted line-total sum (what the customer pays). */
  totalValue: number;
  /** Pre-discount sum, for the strikethrough. */
  totalInitial: number;
  knownPrices: boolean;
  currencyId?: string;
  priceFormat?: string;
  hasSizeSelector: boolean;
  setQty: (value: string, next: number) => void;
  buildItems: () => MultiSizeItem[];
}

/**
 * Encapsulates all multi-size derived state: size→product resolution, price
 * fetching, per-size quantities, totals and the committable item list. Keeps the
 * MultiSizeDrawer component a thin presentation layer.
 *
 * Pricing note: a price tier's `p`/`pi` is the line total for that quantity
 * (matches how the editor header renders getCurrentDesignPrice — no ×qty). `p`
 * is the discounted price, `pi` the pre-discount price for the strikethrough.
 */
const useMultiSizeRows = (): UseMultiSizeRows => {
  const dispatch = useDispatch<ThunkDispatch<Store, void, AnyAction>>();
  const store = useSelector((s: Store) => s);

  const { design } = store;
  const { abstractProduct } = design;
  const categoryId = design.categoryId || abstractProduct?.categoryId || 0;
  const selectedAttributes = design.selectedProductAttributes || {};
  const currencyId = getCurrencyId(store);
  const priceFormat = getPriceFormat(store);

  // Per-unit add-ons applied to every shirt (same design across sizes) — mirrors
  // the editor header (DesignPrice): printing costs + clipart/designs + bundles.
  const productTypeId = getProductTypeId(store);
  const totalPages = getTotalPagesCount(store);
  const minPages = getMinPagesCount(store);
  const clipArtsPrice = getUsedClipArtsPrice(store) || NO_EXTRA;
  const upsellPrice = getUpsellTotalPrice(store) || NO_EXTRA;
  const bundledPrice = getProductBundleSelectedProductsTotalPrice(store) || NO_EXTRA;
  const clipArtEnabled = isFeatureEnabled(store, Feature.supportClipArt);

  const sizeSelector = useMemo(() => findSizeSelector(abstractProduct?.editorSelectors), [abstractProduct]);
  const sizeTypeName = sizeSelector?.typeName || '';
  const sizeOptions = useMemo(() => sizeSelector?.options || [], [sizeSelector]);
  const currentSizeValue = selectedAttributes[sizeTypeName]?.value;
  const variantTypeNames = useMemo(() => getVariantTypeNames(abstractProduct), [abstractProduct]);

  // Price the variant the design is bound to (populated print areas), not the
  // one picked on the product page — see resolveBoundProduct.
  const structure = getStructure(store);
  const boundAttributes = useMemo(
    () => getVariantAttributes(resolveBoundProduct(abstractProduct, structure), selectedAttributes),
    [abstractProduct, structure, selectedAttributes],
  );

  const resolved = useMemo(() => sizeOptions.map((o) => ({
    option: o,
    product: resolveSizeVariant(abstractProduct?.products || [], variantTypeNames, sizeTypeName, o.value, boundAttributes),
  })), [sizeOptions, abstractProduct, boundAttributes, sizeTypeName, variantTypeNames]);

  const sizeProductIds = useMemo(
    () => resolved.map((r) => r.product?.productId).filter((id): id is number => !!id),
    [resolved],
  );

  const currentSizeAvailable = useMemo(
    () => resolved.some((r) => r.option.value === currentSizeValue && !!r.product),
    [resolved, currentSizeValue],
  );

  // Every size floors at 0 — including the PDP-selected one, so a customer who picked
  // the wrong size on the PDP can zero it out and pick a different one in the editor.
  const minQty = 0;

  const [quantities, setQuantities] = useState<{ [value: string]: number }>(() => {
    const seed: { [value: string]: number } = {};
    // The PDP-selected size is what the design was personalised for — it starts at 1
    // (only if it's actually available); other sizes start as optional add-ons at 0.
    sizeOptions.forEach((o) => { seed[o.value] = o.value === currentSizeValue && currentSizeAvailable ? 1 : 0; });
    return seed;
  });

  // Load per-quantity prices for every available size so totals are correct.
  useEffect(() => {
    if (categoryId && sizeProductIds.length > 0) {
      dispatch(fetchPricesOperation(categoryId, sizeProductIds));
    }
  }, [categoryId, sizeProductIds, dispatch]);

  const rows: MultiSizeRow[] = useMemo(() => resolved.map(({ option, product }) => ({
    value: option.value,
    name: option.name || option.value,
    productId: product?.productId,
    available: !!product,
    qty: quantities[option.value] || 0,
    min: minQty,
  })), [resolved, quantities]);

  // Only available rows are committable, so only they count toward the total / CTA.
  const totalUnits = rows.filter((r) => r.available).reduce((sum, r) => sum + r.qty, 0);

  const { totalValue, totalInitial, knownPrices } = useMemo(() => {
    let value = 0;
    let initial = 0;
    let known = true;
    rows.forEach((r) => {
      if (r.qty <= 0 || !r.productId) {
        return;
      }
      // Base p/pi are the line total for this quantity (no ×qty). Then add the
      // per-unit add-ons × qty, exactly like the editor header.
      const entry = getPrice(store, categoryId, r.productId, r.qty);
      if (!entry || !productTypeId) {
        known = false;
        return;
      }
      let line = calculateDesignPrice(entry, productTypeId, minPages, totalPages);
      if (clipArtEnabled) {
        line = addExtraPrice(line, clipArtsPrice, r.qty);
      }
      line = addExtraPrice(line, upsellPrice, r.qty);
      line = addExtraPrice(line, bundledPrice, r.qty);
      value += line.price;
      initial += line.priceInitial;
    });
    return { totalValue: value, totalInitial: initial, knownPrices: known };
  }, [rows, store, categoryId, productTypeId, minPages, totalPages, clipArtEnabled, clipArtsPrice, upsellPrice, bundledPrice]);

  const setQty = (value: string, next: number) => {
    setQuantities((prev) => ({ ...prev, [value]: Math.max(minQty, Math.min(next, 99)) }));
  };

  // Only sizes, not productIds: the design is saved again between this call and
  // the commit, so the concrete variant is resolved once, at commit time.
  const buildItems = (): MultiSizeItem[] => rows
    .filter((r) => r.qty > 0 && r.available)
    .map((r) => ({ sizeValue: r.value, quantity: r.qty }));

  return {
    rows,
    totalUnits,
    totalValue,
    totalInitial,
    knownPrices,
    currencyId,
    priceFormat,
    hasSizeSelector: !!sizeSelector,
    setQty,
    buildItems,
  };
};

export default useMultiSizeRows;
